import { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Navigation, X, Maximize2, ChevronDown, MapPin, Clock, User, Bike } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useRoleContext } from "@/hooks/useRoleContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getBranchZoneEmployees } from "@/services/telecaller";
import { getDeliveryLocation } from "@/services/deliveryAgent";
import { removeWhiteBg } from "@/lib/removeWhiteBg";

const LIBRARIES: ("places")[] = ["places"];
const MINI_MAP = { width: "100%", height: "200px" };
const ICON_SRC = "/delivery-person.jpg";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };

function isDeliveryHours() {
  const h = new Date().getHours();
  return h >= 11 && h < 13;
}

function timeAgo(iso: string) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return new Date(iso).toLocaleTimeString();
}

export default function DeliveryLiveTracker() {
  const { canView } = useScreenPermission("location tracking");
  const { isBranchIncharge, isZoneManager, branchCode, zoneCode } = useRoleContext();
  const userDetails = useCurrentUser();
  const navigate = useNavigate();

  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "").replace(/^['"]|['"]$/g, "");
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey, libraries: LIBRARIES });

  const [show, setShow] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [deliveryBoys, setDeliveryBoys] = useState<{ value: string; label: string }[]>([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [location, setLocation] = useState<{
    lat: number; lng: number; updatedAt: string; username: string;
  } | null>(null);
  const [markerIconUrl, setMarkerIconUrl] = useState(ICON_SRC);
  const [ticker, setTicker] = useState(0); // ticks every second for "Xs ago" display

  // Refs — stable across renders, no re-render side-effects
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const usernameRef = useRef("");
  const fetchingRef = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Keep ref in sync with state (no effect needed)
  usernameRef.current = selectedUsername;

  // Remove white bg from delivery person image once
  useEffect(() => {
    removeWhiteBg(ICON_SRC).then(setMarkerIconUrl);
  }, []);

  // Show/hide based on delivery hours
  useEffect(() => {
    if (canView) setShow(isDeliveryHours());
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    const id = setInterval(() => setShow(isDeliveryHours()), 60000);
    return () => clearInterval(id);
  }, [canView]);

  // Ticker for "Xs ago" — runs every second, lightweight
  useEffect(() => {
    tickerRef.current = setInterval(() => setTicker(t => t + 1), 1000);
    return () => { if (tickerRef.current) clearInterval(tickerRef.current); };
  }, []);

  // Fetch delivery boys list once when shown
  useEffect(() => {
    if (!show || !canView) return;
    (async () => {
      try {
        const params = isBranchIncharge && branchCode
          ? { Branch_Code: branchCode }
          : isZoneManager && zoneCode
            ? { Zone_Code: zoneCode }
            : undefined;
        const res = await getBranchZoneEmployees("delivery", params);
        const data: any[] = Array.isArray(res.data) ? res.data : [];
        const boys = data.map(e => ({ value: e.user__username, label: e.Emp_Name || e.user__username }));
        setDeliveryBoys(boys);
        // Auto-select first boy only if not yet selected
        if (boys.length > 0 && !usernameRef.current) {
          setSelectedUsername(boys[0].value);
        }
      } catch { }
    })();
  }, [show, canView, isBranchIncharge, isZoneManager, branchCode, zoneCode]);

  // Core fetch — uses ref so interval closure is always fresh
  const fetchLoc = useCallback(async () => {
    const username = usernameRef.current;
    if (!username || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await getDeliveryLocation(username);
      const d = res.data as any;
      const loc = Array.isArray(d) ? d[0] : d;
      if (loc?.latitude && loc?.longitude) {
        const newLat = Number(loc.latitude);
        const newLng = Number(loc.longitude);
        setLocation(prev => {
          // Only update + pan if position actually changed
          if (prev && prev.lat === newLat && prev.lng === newLng && prev.username === username) return prev;
          const newPos = { lat: newLat, lng: newLng, updatedAt: loc.updated_at || new Date().toISOString(), username };
          mapRef.current?.panTo({ lat: newLat, lng: newLng });
          return newPos;
        });
      }
    } catch { }
    finally { fetchingRef.current = false; }
  }, []);

  // Start/stop interval when username changes — don't recreate if same username
  useEffect(() => {
    if (!selectedUsername) return;
    // Immediate fetch on selection change
    fetchLoc();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchLoc, 2000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [selectedUsername, fetchLoc]);

  // When delivery boy changes, just clear old location (map will update on next fetch)
  const handleUserChange = (username: string) => {
    setLocation(null);
    setSelectedUsername(username);
  };

  const icon = isLoaded && window.google
    ? { url: markerIconUrl, scaledSize: new window.google.maps.Size(40, 40), anchor: new window.google.maps.Point(20, 40) }
    : undefined;

  if (!show || !canView) return null;

  const selectedLabel = deliveryBoys.find(b => b.value === selectedUsername)?.label || selectedUsername;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {minimized ? (
        // ── Pill ──────────────────────────────────────────────────────────────
        <button
          onClick={() => setMinimized(false)}
          className="flex items-center gap-2.5 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl shadow-2xl transition-all"
        >
          <span className="w-2 h-2 bg-white rounded-full animate-pulse flex-shrink-0" />
          <Bike className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-bold">Live Tracking</span>
          {location && (
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
              {timeAgo(location.updatedAt)}
            </span>
          )}
        </button>
      ) : (
        // ── Expanded card ──────────────────────────────────────────────────────
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" style={{ width: 320 }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <Bike className="w-4 h-4 text-white" />
              <span className="text-sm font-bold text-white">Live Delivery</span>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => navigate("/track-delivery")}
                title="Full tracking view"
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Maximize2 className="w-3.5 h-3.5 text-white" />
              </button>
              <button onClick={() => setMinimized(true)} title="Minimize" className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                <ChevronDown className="w-3.5 h-3.5 text-white" />
              </button>
              <button onClick={() => setShow(false)} title="Close" className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          </div>

          {/* Delivery boy selector */}
          {deliveryBoys.length > 0 && (
            <div className="px-3 pt-3">
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <select
                  value={selectedUsername}
                  onChange={e => handleUserChange(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm font-medium border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-gray-50 appearance-none"
                >
                  {deliveryBoys.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Map */}
          <div className="px-3 pt-3">
            <div className="relative rounded-xl overflow-hidden border border-gray-100" style={{ height: 200 }}>
              {!isLoaded || (!location && !selectedUsername) ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 gap-2">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-orange-400" />
                  </div>
                  <p className="text-xs text-gray-400">Select a delivery boy</p>
                </div>
              ) : !location ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-orange-500 border-t-transparent" />
                  <p className="text-xs text-gray-400">Fetching location…</p>
                </div>
              ) : (
                <>
                  <GoogleMap
                    mapContainerStyle={MINI_MAP}
                    center={location}
                    zoom={15}
                    onLoad={map => { mapRef.current = map; }}
                    options={{
                      mapTypeControl: false, fullscreenControl: false,
                      streetViewControl: false, zoomControl: false,
                      gestureHandling: "cooperative",
                      styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
                    }}
                  >
                    <Marker position={location} icon={icon} />
                  </GoogleMap>

                  {/* Username badge overlay on map */}
                  <div className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-md">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
                    <span className="text-xs font-bold text-gray-800 capitalize">{selectedLabel}</span>
                  </div>

                  {/* Last updated badge */}
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/50 px-2 py-1 rounded-lg">
                    <Clock className="w-2.5 h-2.5 text-white" />
                    <span className="text-[10px] text-white font-medium">{timeAgo(location.updatedAt)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Info row */}
          <div className="px-3 pt-2.5 pb-3 space-y-2">
            {location ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    <p className="text-xs font-semibold text-gray-800 capitalize">{selectedLabel}</p>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400">
                    <Clock className="w-3 h-3" />
                    <p className="text-xs">{timeAgo(location.updatedAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5">
                  <MapPin className="w-3 h-3 text-orange-400 flex-shrink-0" />
                  <p className="text-xs font-mono text-gray-600">
                    {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
                  </p>
                </div>
                <div className="text-xs text-gray-400 text-right">
                  {new Date(location.updatedAt).toLocaleString()}
                </div>
              </>
            ) : selectedUsername ? (
              <p className="text-xs text-gray-400 text-center py-1">Waiting for location…</p>
            ) : null}

            {/* Full view button */}
            <button
              onClick={() => navigate("/track-delivery")}
              className="w-full flex items-center justify-center gap-2 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-xl transition-colors"
            >
              <Navigation className="w-3.5 h-3.5" />
              Open Full Tracking
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
